import { useState, useEffect } from 'react';
import { styled } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import CircularProgress from '@mui/material/CircularProgress';
import { useMyTransactionsSummaryContext } from './MyTransactionsSummaryContext';
import MyTransactionsSummaryForm, {
  FilterOptions
} from './MyTransactionsSummaryForm';
import MyTransactionsSummaryTable from './MuiEnhancedTable';
import MyTransactionSummaryDetails from './MyTransactionSummaryDetails';
import PatientStatement from './PatientStatement';
import { InvoiceDocumentsDialog } from 'components/InvoiceDocumentsDialog/InvoiceDocumentsDialog';
import { PartnerGetMyTransactionSummary } from './constants';
import SessionTimeout from 'components/SessionTimeout';
import { PatientInsuranceDialog } from './PatientInsuranceDialog/PatientInsuranceDialog';
import { Divider } from '@mui/material';
import { useSnackbar } from 'notistack';
import { theme } from 'lib/theme';
import { useMswManager } from 'lib/util/useMswManager';
import { HrznGatewayMswBuilder } from 'stories/HrznGatewayMswBuilder';
import { ROUTES } from 'constants/routes';
import makeData from './makeData';

const Layout = styled('div')`
  max-width: 2048px;
  margin: 0 auto;
  padding: 0 30px;
`;

/**
 * Main MyTransactionsSummary component
 * Displays and manages transaction data
 * @returns {JSX.Element}
 */
function MyTransactionsSummary() {
  const { enqueueSnackbar } = useSnackbar();
  const {
    encounters,
    setEncounters,
    setFacilityOptions,
    facility,
    advancedFilter,
    value1,
    value2,
    search,
    setSearch,
    isLoading,
    setIsLoading,
    providerSearch,
    notSearched,
    setNotSearched
  } = useMyTransactionsSummaryContext();
  const [
    showMyTransactionsX12DocumentLink,
    setShowMyTransactionsX12DocumentLink
  ] = useState(false);
  const [openSummary, setOpenSummary] = useState(null);
  const [openPatientStatement, setOpenPatientStatement] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [openPatientInsurance, setOpenPatientInsurance] = useState({});
  const [message, setMessage] = useState('');

  useMswManager([
    transactionSummaryHandler,
    documentHeadersHandler,
    documentDetailsHandler,
    documentEdmHandler
  ]);

  useEffect(() => {
    const fetchOptions = async () => {
      const user = JSON.parse(await sessionStorage.getItem('user'));
      const facilityIDs = user.facilities.map(
        ({ facilityID, facilityName }) => ({
          value: facilityID,
          label: facilityName
        })
      );
      setFacilityOptions(facilityIDs);

      user.showMyTransactionsX12DocumentLink = true;

      setShowMyTransactionsX12DocumentLink(
        user.showMyTransactionsX12DocumentLink
      );
    };
    fetchOptions();
  }, []);

  const formatValue1 = (value, filterValue) => {
    if (!value || !filterValue) return '';
    if (filterValue === FilterOptions.dob)
      return moment(value).format('MM-DD-YYYY');
    if (filterValue === FilterOptions.dosRange) {
      return moment(value).format('MM-DD-YYYY');
    }
    return value;
  };

  const formatValue2 = (value, filterValue) => {
    if (!value || !filterValue) return '';
    if (filterValue === FilterOptions.dosRange) {
      return moment(value).format('MM-DD-YYYY');
    }
    return value;
  };

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        facilityIds: facility.map(el => el.value),
        key: mapAdvancedFilter(advancedFilter),
        value1: formatValue1(value1, advancedFilter),
        value2: formatValue2(value2, advancedFilter),
        providerList: providerSearch ? [providerSearch] : []
      };
      const config = {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('usertoken')}`
        }
      };
      const URL = `${process.env.REACT_APP_URL}/?route=${PartnerGetMyTransactionSummary}`;
      const isFetching = search;
      try {
        const result = isFetching
          ? await axios.post(URL, payload, config)
          : encounters;
        setEncounters(isFetching ? result.data : result);
        setNotSearched(isFetching ? false : notSearched);
        setSearch(false);
      } catch (err) {
        setMessage(err ? err?.response?.data.Error : message);
        if (err?.response?.data?.Error) {
          enqueueSnackbar(err?.response?.data?.Error, {
            variant: 'error'
          });
        }
        setIsLoading(false);
        setSearch(false);
        setNotSearched(false);
        setEncounters([]);
      }
    };
    fetchData();
  }, [search]);

  useEffect(() => {
    setIsLoading(false);
  }, [encounters]);

  return (
    <Layout>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
        >
          <h1 style={{ marginRight: '30px' }}>My Transactions</h1>
          <p
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: theme.palette.primary.main
            }}
          >
            Please select Search Type criteria and click Search
          </p>
        </div>
        {/* eslint-disable-next-line react/jsx-no-target-blank */}
        <a
          style={{ fontSize: '16px' }}
          href={`https://mgablobstorageprodcdn.blob.core.windows.net/${process.env.REACT_APP_NODE_ENV.toLowerCase()}-cdn/Partner.Gottlieb.com.UserGuide.MyTransactions.pdf?id=${Math.random()
            .toString(36)
            .slice(2)}`}
          target="_blank"
        >
          User Guide
        </a>
      </div>
      <>
        <MyTransactionsSummaryForm />

        <Divider
          variant="middle"
          style={{ marginTop: '2em', marginBottom: '1em' }}
        />
      </>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '15px'
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div style={{ display: notSearched ? 'none' : 'block' }}>
          <MyTransactionsSummaryTable
            tableData={
              encounters?.map(encounter => {
                return {
                  ...encounter,
                  ados: encounter.ados
                    ? moment(encounter.ados).format('MM/DD/YY')
                    : '',
                  dob: encounter.dob
                    ? moment(encounter.dob).format('MM/DD/YY')
                    : ''
                };
              }) || []
            }
            openPatientInsurance={openPatientInsurance}
            setOpenPatientInsurance={setOpenPatientInsurance}
            notSearched={notSearched}
            openSummary={openSummary}
            openPatientStatement={openPatientStatement}
            showMyTransactionsX12DocumentLink={
              showMyTransactionsX12DocumentLink
            }
            openInvoice={openInvoice}
            showInvoice={showInvoice}
            setOpenSummary={setOpenSummary}
            setOpenPatientStatement={setOpenPatientStatement}
            setOpenInvoice={setOpenInvoice}
            setShowInvoice={setShowInvoice}
            setShowMyTransactionsX12DocumentLink={
              setShowMyTransactionsX12DocumentLink
            }
          />
        </div>
      )}

      {!!showMyTransactionsX12DocumentLink && !!showInvoice ? (
        <InvoiceDocumentsDialog
          open={!!openInvoice}
          invoiceNo={showInvoice}
          onClose={() => setOpenInvoice(false)}
        ></InvoiceDocumentsDialog>
      ) : null}

      {!!openSummary && (
        <MyTransactionSummaryDetails
          open={!!openSummary}
          accountNumber={
            openSummary
              ? {
                  account: openSummary.account,
                  srcSystemKey: openSummary.srcSystemKey
                }
              : null
          }
          facility={openSummary ? openSummary.facility : null}
          close={() => setOpenSummary(null)}
        />
      )}
      {!!openPatientStatement && (
        <PatientStatement
          open={!!openPatientStatement}
          accountNumber={
            openPatientStatement
              ? {
                  account: openPatientStatement.account,
                  srcSystemKey: openPatientStatement.srcSystemKey
                }
              : null
          }
          close={() => setOpenPatientStatement(null)}
        />
      )}
      {!!openPatientInsurance && (
        <PatientInsuranceDialog
          invoiceNo={openPatientInsurance.invoiceNo}
          srcSystemKey={openPatientInsurance.srcSystemKey}
          open={Boolean(openPatientInsurance.invoiceNo)}
          close={() => setOpenPatientInsurance({})}
        />
      )}
      <SessionTimeout />
    </Layout>
  );
}

export default MyTransactionsSummary;

const mapAdvancedFilter = advancedFilter => {
  switch (advancedFilter) {
    case 'Date Of Birth':
      return 'PatientDOB';
    case 'Patient Last Name':
      return 'LastName';
    case 'Account Number':
      return 'FacilityAccountNumber';
    case 'Statement Account':
      return 'statementAccount';
    case 'Invoice Number':
      return 'invoiceNumber';
    case 'Provider':
      return 'Provider';
    case 'Date Of Service Range':
      return 'DateOfService';
    default:
      return '';
  }
};

/**
 * Handler that returns mock transaction data
 * @type {import('stories/HrznGatewayMswBuilder').HrznGatewayMswBuilder}
 */
const transactionSummaryHandler = HrznGatewayMswBuilder.create().post(
  PartnerGetMyTransactionSummary,
  {
    /**
     * @type {import('src/types/transaction').Transaction[]}
     */
    data: makeData()
  }
);

const documentHeadersHandler = HrznGatewayMswBuilder.create().post(
  ROUTES.GetDocumentHeaders,
  {
    data: [
      {
        documentHeaderId: 'doc1',
        documentTypeId: 1,
        documentAliasName: 'Sample X12 Document 1',
        showDataInRawFormat: true,
        createdDateTime: '2024-03-20T10:00:00'
      },
      {
        documentHeaderId: 'doc2',
        documentTypeId: 2,
        documentAliasName: 'Sample PDF Document',
        showDataInRawFormat: false,
        createdDateTime: '2024-03-19T15:30:00'
      }
    ]
  }
);

const documentDetailsHandler = HrznGatewayMswBuilder.create().post(
  ROUTES.GetDocumentDetails,
  {
    data: [
      {
        segmentKey: 'ISA',
        segmentValue:
          '00*          *00*          *ZZ*123456789      *ZZ*987654321'
      },
      {
        segmentKey: 'GS',
        segmentValue: 'HC*123456789*987654321*20240320*1000*1234*X*005010X222A1'
      },
      {
        segmentKey: 'ST',
        segmentValue: '837*0001*005010X222A1'
      }
    ]
  }
);

const documentEdmHandler = HrznGatewayMswBuilder.create().post(
  ROUTES.GetDocumentEdmById,
  {
    data: {
      byteArray:
        'JVBERi0xLjcKCjEgMCBvYmogICUgZW50cnkgcG9pbnQKPDwKICAvVHlwZSAvQ2F0YWxvZwogIC9QYWdlcyAyIDAgUgo+PgplbmRvYmoKCjIgMCBvYmoKPDwKICAvVHlwZSAvUGFnZXMKICAvTWVkaWFCb3ggWyAwIDAgMjAwIDIwMCBdCiAgL0NvdW50IDEKICAvS2lkcyBbIDMgMCBSIF0KPj4KZW5kb2JqCgozIDAgb2JqCjw8CiAgL1R5cGUgL1BhZ2UKICAvUGFyZW50IDIgMCBSCiAgL1Jlc291cmNlcyA8PAogICAgL0ZvbnQgPDwKICAgICAgL0YxIDQgMCBSIAogICAgPj4KICA+PgogIC9Db250ZW50cyA1IDAgUgo+PgplbmRvYmoKCjQgMCBvYmoKPDwKICAvVHlwZSAvRm9udAogIC9TdWJ0eXBlIC9UeXBlMQogIC9CYXNlRm9udCAvVGltZXMtUm9tYW4KPj4KZW5kb2JqCgo1IDAgb2JqICAlIHBhZ2UgY29udGVudAo8PAogIC9MZW5ndGggNDQKPj4Kc3RyZWFtCkJUCjcwIDUwIFRECi9GMSAxMiBUZgooSGVsbG8sIHdvcmxkISkgVGoKRVQKZW5kc3RyZWFtCmVuZG9iagoKeHJlZgowIDYKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMDEwIDAwMDAwIG4gCjAwMDAwMDAwNzkgMDAwMDAgbiAKMDAwMDAwMDE3MyAwMDAwMCBuIAowMDAwMDAwMzAxIDAwMDAwIG4gCjAwMDAwMDAzODAgMDAwMDAgbiAKdHJhaWxlcgo8PAogIC9TaXplIDYKICAvUm9vdCAxIDAgUgo+PgpzdGFydHhyZWYKNDkyCiUlRU9G',
      mimeType: 'application/pdf'
    }
  }
);
